import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Check, X, AlertTriangle, Info } from 'lucide-react';
import { ToastItem } from './toast-types';

/**
 * Callers pass caught values straight through (`toast.error(err)`), and React
 * throws on an Error or plain object rendered as a child — which unmounts the
 * whole app. Anything that is not renderable is turned into text here, so a
 * failed request shows a message instead of blanking the page.
 */
const toastText = (value: React.ReactNode): React.ReactNode => {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (React.isValidElement(value) || Array.isArray(value)) return value;
  if (value instanceof Error) return value.message || String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

interface ToastCardProps {
  toast: ToastItem;
  onDismiss: (id: string | number) => void;
}

export const ToastCard: React.FC<ToastCardProps> = ({ toast, onDismiss }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [remainingTime, setRemainingTime] = useState(
    toast.duration && isFinite(toast.duration) ? toast.duration : 0,
  );
  const startTimeRef = useRef<number>(Date.now());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!toast.duration || !isFinite(toast.duration) || toast.duration <= 0) {
      return;
    }

    if (isPaused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      return;
    }

    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      onDismiss(toast.id);
      if (toast.onClose) toast.onClose();
    }, remainingTime);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isPaused, remainingTime, toast, onDismiss]);

  const handleMouseEnter = () => {
    if (toast.pauseOnHover !== false && toast.duration && isFinite(toast.duration)) {
      const elapsed = Date.now() - startTimeRef.current;
      setRemainingTime((prev) => Math.max(0, prev - elapsed));
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (toast.pauseOnHover !== false && toast.duration && isFinite(toast.duration)) {
      setIsPaused(false);
    }
  };

  // Status icon & accents
  const getTypeConfig = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: 'rgba(16, 185, 129, 0.2)',
                color: '#34d399',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Check size={12} strokeWidth={3} />
            </Box>
          ),
          borderColor: 'rgba(51, 65, 85, 0.8)', hoverBorderColor: 'rgba(16, 185, 129, 0.4)',
        };
      case 'error':
        return {
          icon: (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: 'rgba(244, 63, 94, 0.2)',
                color: '#fb7185',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <X size={12} strokeWidth={3} />
            </Box>
          ),
          borderColor: 'rgba(51, 65, 85, 0.8)', hoverBorderColor: 'rgba(244, 63, 94, 0.4)',
        };
      case 'warning':
        return {
          icon: (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: 'rgba(245, 158, 11, 0.2)',
                color: '#fbbf24',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AlertTriangle size={12} strokeWidth={2.5} />
            </Box>
          ),
          borderColor: 'rgba(51, 65, 85, 0.8)', hoverBorderColor: 'rgba(245, 158, 11, 0.4)',
        };
      case 'info':
        return {
          icon: (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: 'rgba(14, 165, 233, 0.2)',
                color: '#38bdf8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Info size={12} strokeWidth={2.5} />
            </Box>
          ),
          borderColor: 'rgba(51, 65, 85, 0.8)', hoverBorderColor: 'rgba(14, 165, 233, 0.4)',
        };
      default:
        return {
          icon: (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: '#334155',
                color: '#cbd5e1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Info size={12} strokeWidth={2.5} />
            </Box>
          ),
          borderColor: 'rgba(51, 65, 85, 0.8)', hoverBorderColor: 'rgba(51, 65, 85, 0.8)',
        };
    }
  };

  const config = getTypeConfig();
  const displayIcon = toast.icon !== undefined ? toast.icon : config.icon;
  const isTop = (toast.position || 'top-right').startsWith('top');

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: isTop ? -24 : 24, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{
        opacity: 0,
        scale: 0.9,
        y: isTop ? -16 : 16,
        transition: { duration: 0.18, ease: 'easeOut' },
      }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30,
        mass: 0.8,
      }}
      drag="x"
      dragConstraints={{ left: 0, right: 300 }}
      dragElastic={{ left: 0.05, right: 0.8 }}
      onDragEnd={(_, info) => {
        if (info.offset.x > 90 || info.velocity.x > 350) {
          onDismiss(toast.id);
          if (toast.onClose) toast.onClose();
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => {
        if (toast.onClick) toast.onClick();
        if (toast.closeOnClick) {
          onDismiss(toast.id);
          if (toast.onClose) toast.onClose();
        }
      }}
      className={toast.className || ''}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        minWidth: 320,
        maxWidth: 440,
        width: '100%',
        padding: '14px 16px',
        borderRadius: 12,
        backgroundColor: 'rgba(12, 18, 30, 0.95)',
        backdropFilter: 'blur(16px)',
        border: '1px solid ' + config.borderColor,
        boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.06)',
        color: '#f1f5f9',
        transition: 'border-color 0.2s ease',
        pointerEvents: 'auto',
        userSelect: 'none',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, flex: 1 }}>
        {displayIcon && <Box sx={{ mt: 0.25 }}>{displayIcon}</Box>}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          {toast.title ? (
            <>
              <Typography
                noWrap
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#f1f5f9',
                  lineHeight: 1.2,
                }}
              >
                {toast.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#94a3b8',
                  mt: 0.25,
                  lineHeight: 1.35,
                  wordBreak: 'break-word',
                }}
              >
                {toastText(toast.message)}
              </Typography>
            </>
          ) : (
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: '#f1f5f9',
                lineHeight: 1.35,
                wordBreak: 'break-word',
              }}
            >
              {toastText(toast.message)}
            </Typography>
          )}
          {toast.description && (
            <Typography
              sx={{
                fontSize: '11px',
                color: '#94a3b8',
                mt: 0.5,
                lineHeight: 1.4,
              }}
            >
              {toast.description}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Action Button */}
      {toast.action && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toast.action?.onClick(e);
            onDismiss(toast.id);
          }}
          className={toast.action.className || ''}
          sx={{
            flexShrink: 0,
            fontSize: '0.75rem',
            fontWeight: 600,
            px: 1.5,
            py: 0.75,
            borderRadius: '8px',
            bgcolor: '#4eed9e',
            color: '#081812',
            textTransform: 'none',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            '&:hover': { bgcolor: '#3be090' },
            '&:active': { transform: 'scale(0.95)' },
          }}
        >
          {toast.action.label}
        </Button>
      )}

      {/* Dismiss Button */}
      {toast.dismissible && !toast.action && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(toast.id);
            if (toast.onClose) toast.onClose();
          }}
          sx={{
            flexShrink: 0,
            p: 0.5,
            color: '#64748b',
            '&:hover': { color: '#cbd5e1' },
            opacity: 0.7,
            '&:hover, .group:hover &': { opacity: 1 },
          }}
          aria-label="Close notification"
        >
          <X size={16} />
        </IconButton>
      )}
    </motion.div>
  );
};

export default ToastCard;
